import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _has from "lodash/has";
import _each from "lodash/each";
import _transform from "lodash/transform";
import _map from "lodash/map";
import _debounce from "lodash/debounce";
import _omit from "lodash/omit";
import _set from "lodash/set";
import _isFunction from "lodash/isFunction";
import _get from "lodash/get";
import _memoize from "lodash/memoize";
import "regenerator-runtime/runtime.js";
import "core-js/modules/es6.object.to-string.js";
import "core-js/modules/es6.promise.js";
import "core-js/modules/es6.string.iterator.js";
import "core-js/modules/es6.array.iterator.js";
import "core-js/modules/web.dom.iterable.js";
import "core-js/modules/es6.object.keys.js";
import PropTypes from 'prop-types';
import serializeError from 'serialize-error';
import hash from '../lib/hash';
import defer from '../lib/defer';
import createContextStore, { propType as contextStorePropType } from './context-store';
export default function createDataProviders(_ref) {
  var providers = _ref.providers,
      _ref$contextStore = _ref.contextStore,
      contextStore = _ref$contextStore === void 0 ? createContextStore() : _ref$contextStore;
  var store = contextStore; // holds reference to requests inflight for de-duping

  var deferredRequests = {}; // holds reference to descriptions that will be loaded on next tick

  var deferredRequestsQueue = {}; // loads provider module
  //  memoized to cache providers initialized with options

  var loadProvider = _memoize(function (type) {
    return new Promise(function (resolve, reject) {
      var resolveProvider = _get(providers, type);

      if (_isFunction(resolveProvider) === false) throw new Error("no provider defined for type: '".concat(type, "'"));
      return resolveProvider().then(resolve, reject);
    });
  }); // returns a promise to load data for provided description


  function createDeferredRequest(id, description) {
    // create a Promise that can be resolved/rejected out of scope
    // to register as the `deferredRequest` for this description id
    var deferred = defer();

    _set(deferredRequests, id, deferred); // register this data description in the queue to be loaded on next tick


    _set(deferredRequestsQueue, id, description); // set the loading state for this description


    store.set(id, {
      loading: true
    });
    return deferred.promise;
  }

  function resolveDeferredRequest(id, value) {
    var deferred = deferredRequests[id];
    delete deferredRequests[id]; // set the response value in the store for this description

    store.set(id, {
      loading: false,
      value: value
    }); // resolve with node-callback-style success arguments

    deferred.resolve([null, value]);
  }

  function rejectDeferredRequest(id, error) {
    var deferred = deferredRequests[id];
    delete deferredRequests[id];
    var safeError = error;

    if (safeError && safeError instanceof Error) {
      safeError = serializeError(error);

      if (process.env.NODE_ENV !== 'development') {
        safeError = _omit(safeError, ['stack']);
      }
    } // set the error state for this description


    store.set(id, {
      loading: false,
      error: safeError
    }); // resolve with node-callback-style error arguments

    deferred.resolve([error, null]);
  } // loads queued batches of descriptions from individual providers
  // registers promises in `requests` dict for each description
  // updates the data store upon request resolution/rejection


  var flushDeferredDescriptionsQueue = _debounce(function () {
    var ids = _map(deferredRequestsQueue, function (description, id) {
      return id;
    }); // group collections of id and description by description `type`


    var descriptionsByType = _transform(ids, function (memo, id) {
      var description = deferredRequestsQueue[id];
      var type = description.type;

      var list = _get(memo, type, []);

      list.push({
        id: id,
        description: description
      });

      _set(memo, type, list);

      delete deferredRequestsQueue[id];
    }, {}); // go through the different type groups and send batch load requests to individual providers


    _each(descriptionsByType, function (descriptionsAndIds, type) {
      // prepare batch of descriptions to pass to individual provider
      var descriptions = _map(descriptionsAndIds, function (_ref2) {
        var description = _ref2.description;
        return description;
      }); // load the provider module and pass the batch of descriptions to its `load(descriptions)` method


      var providerLoadPromise = loadProvider(type).then(function (provider) {
        return provider.load(descriptions);
      }); // prepare the promises to store in the `requests` dict
      // eslint-disable-next-line no-unused-vars

      _each(descriptionsAndIds, function (_ref3, i) {
        var id = _ref3.id,
            description = _ref3.description;
        // wait for the promised promises returned from the individual provider's load method
        providerLoadPromise.then(function (promises) {
          var descriptionLoadPromise = promises[i]; // attach handlers to the individual promise for this particular description
          // to resolve the deferred object for the promise inserted in the `deferredRequests` dictionary

          descriptionLoadPromise.then(function (res) {
            return resolveDeferredRequest(id, res);
          }, function (err) {
            return rejectDeferredRequest(id, err);
          });
        });
      });
    });
  }); // insert description into queue to load
  // returns promise that will be resolved with node style (err, data) callback arguments
  // de-dupes for inflight and queued descriptions


  function queueDescriptionRequest(id, description) {
    if (_has(deferredRequests, id)) return deferredRequests[id].promise;
    return createDeferredRequest(id, description);
  } // initiates requests for descriptions not already loading
  // returns a collection containing id and promise for each supplied description


  function loadDescriptions(descriptions) {
    // create or retrieve promise for each description matching the order passed in
    var collection = _map(descriptions, function (description) {
      var id = hash(description);
      var promise = queueDescriptionRequest(id, description);
      return {
        id: id,
        promise: promise
      };
    }); // call the debounced flush method to send batch requests to individual providers


    flushDeferredDescriptionsQueue(); // return a collection of [{ id, promise }] that
    // matches the order of the descriptions provided to this method

    return collection;
  }

  function load(descriptions) {
    var collection = loadDescriptions(descriptions);
    return Promise.all(_map(collection, function (_ref4) {
      var promise = _ref4.promise;
      return promise;
    }));
  }

  function hasPending() {
    return Object.keys(deferredRequests).length > 0;
  }

  function waitForPending() {
    return _waitForPending.apply(this, arguments);
  }

  function _waitForPending() {
    _waitForPending = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee() {
      return regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return Promise.all(_map(deferredRequests, function (_ref5) {
                var promise = _ref5.promise;
                return promise;
              }));

            case 2:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));
    return _waitForPending.apply(this, arguments);
  }

  function extract() {
    return store.get();
  }

  function fill(data) {
    return store.set(data);
  }

  return {
    store: store,
    load: load,
    hasPending: hasPending,
    waitForPending: waitForPending,
    extract: extract,
    fill: fill,
    // for tests
    loadDescriptions: loadDescriptions
  };
}
export var propType = PropTypes.shape({
  load: PropTypes.func.isRequired,
  store: contextStorePropType.isRequired,
  hasPending: PropTypes.func.isRequired,
  waitForPending: PropTypes.func.isRequired,
  extract: PropTypes.func.isRequired,
  fill: PropTypes.func.isRequired
});
export var dataDependencyPropType = PropTypes.shape({
  loading: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  error: PropTypes.object,
  // TODO: better validator if possible
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.any // TODO: better validator if possible

});
export var storeStatePropType = PropTypes.objectOf(dataDependencyPropType);